import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock, Award, ArrowRight, BookOpen, Cpu, Users, Target } from 'lucide-react';

const CertificateCourses = () => {
  const certificates = [
    {
      id: 'ai-humanitarian',
      title: 'AI in Humanitarian Action & Development',
      duration: '4 Weeks',
      fullFee: '$500',
      scholarshipFee: '$50',
      description: 'Learn how artificial intelligence and machine learning are transforming humanitarian response, from predictive analytics to resource optimization.',
      link: '/certificate/ai-humanitarian',
      highlights: [
        'AI fundamentals for humanitarian contexts',
        'Machine learning applications in crisis response',
        'Ethical considerations in humanitarian AI',
        'Hands-on projects with real-world datasets'
      ],
      icon: Cpu
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="certificate-courses-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Professional Development
            </p>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Certificate Courses
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
              Short-term, intensive courses designed to build specialized skills in emerging areas 
              of humanitarian action and development. Perfect for professionals seeking to upskill 
              or explore new domains.
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock size={18} className="text-[#163A59]" />
                <span>4-8 Week Programs</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Award size={18} className="text-[#163A59]" />
                <span>Certificate of Completion</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Target size={18} className="text-[#163A59]" />
                <span>90% Scholarship Available</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Certificate Courses */}
      <section className="py-16 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8 text-white text-center">
            <div>
              <Clock className="w-10 h-10 mx-auto mb-4 opacity-80" />
              <h3 className="font-semibold text-lg mb-2">Flexible Duration</h3>
              <p className="text-sm opacity-80">Short-term courses that fit your schedule</p>
            </div>
            <div>
              <BookOpen className="w-10 h-10 mx-auto mb-4 opacity-80" />
              <h3 className="font-semibold text-lg mb-2">Practical Skills</h3>
              <p className="text-sm opacity-80">Hands-on learning with real-world applications</p>
            </div>
            <div>
              <Users className="w-10 h-10 mx-auto mb-4 opacity-80" />
              <h3 className="font-semibold text-lg mb-2">Expert Faculty</h3>
              <p className="text-sm opacity-80">Learn from industry practitioners</p>
            </div>
          </div>
        </div>
      </section>

      {/* Available Certificates */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Available Courses
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900">
              Our Certificate Programs
            </h2>
          </div>

          <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {certificates.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-[#DCE4EA] hover:border-[#163A59]/30 hover:shadow-xl transition-all"
              >
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-[#163A59]/10 flex items-center justify-center rounded-sm">
                      <cert.icon className="w-7 h-7 text-[#163A59]" />
                    </div>
                    <div>
                      <h3 className="font-['Source_Serif_4'] text-xl font-bold text-slate-900">
                        {cert.title}
                      </h3>
                      <p className="text-sm text-slate-500">{cert.duration} • Certificate</p>
                    </div>
                  </div>

                  <p className="text-slate-600 mb-6">{cert.description}</p>

                  <div className="mb-6">
                    <p className="text-sm font-medium text-slate-700 mb-3">What you'll learn:</p>
                    <ul className="space-y-2">
                      {cert.highlights.map((highlight, i) => (
                        <li key={`highlight-${highlight.slice(0, 10)}`} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="w-1.5 h-1.5 bg-[#163A59] rounded-full mt-2 flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-[#DCE4EA]">
                    <div>
                      <p className="text-sm text-slate-500">Tuition</p>
                      <p className="font-semibold text-slate-900">{cert.fullFee}</p>
                    </div>
                    <Link
                      to={cert.link}
                      className="inline-flex items-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-6 py-3 rounded-sm font-medium text-sm transition-all"
                    >
                      Learn More
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Coming Soon Notice */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 text-sm">
              More certificate courses coming soon. Stay tuned for updates!
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-white mb-6">
            Looking for a Degree Program?
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
            We also offer Master's and Doctoral programs in Humanitarian Action & Development. 
            Explore our comprehensive degree programs for a deeper academic journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/programs"
              className="inline-flex items-center justify-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-10 py-4 rounded-sm font-medium transition-all"
            >
              View Degree Programs
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white hover:bg-white/10 px-10 py-4 rounded-sm font-medium transition-all"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CertificateCourses;
